/**
 * Deelnemers — the people with a confirmed booking on an activity's tijdsloten.
 *
 * Fed by `GET /v1/activities/{id}/participants`, the only read that carries
 * participant identities (ADR-0059). The display reads no longer do, so nothing
 * here may fall back to `slot.enrolls` — those rows have no names any more.
 *
 * Deliberately import-free — the standalone `scripts/test-*.mjs` runner
 * evaluates this file in a bare vm context.
 */

/**
 * Whether an activity shows its Deelnemers — the rule `ParticipantsService::
 * enabledFor` enforces in Laravel. The activity's own `show_participants`
 * (Zichtbaar/Verborgen) wins; absent or null is "Volg bedrijfspagina", and the
 * page is on when it says nothing (ClickUp 1240ccecgzt, ADR-0059). This only
 * keeps the button from flashing up — the server refuses the read itself.
 */
export function participantsVisible(pageSettings, activityContent) {
  const own = activityContent?.show_participants;
  if (own !== undefined && own !== null) return Boolean(own);

  const page = pageSettings?.show_participants;

  return page === undefined || page === null ? true : Boolean(page);
}

/**
 * Dutch tussenvoegsels, skipped when building initials: "Kees de Jong" is KJ,
 * not KD. Stored without punctuation so "'t" and "t" both match.
 */
const TUSSENVOEGSELS = new Set([
  "de", "den", "der", "des", "het", "t", "s", "d",
  "van", "von", "ver", "vd", "vande", "vanden", "vander",
  "te", "ten", "ter", "op", "aan", "bij", "in", "onder", "over", "uit",
  "voor", "tot", "af", "au", "du", "la", "le", "les", "del", "della", "di", "da", "do", "dos", "el",
]);

const normalise = (word) =>
  String(word || "")
    .toLowerCase()
    .replace(/[^a-zà-ÿ]/g, "");

/**
 * The first letter of a surname, skipping any leading tussenvoegsel. Falls back
 * to the raw first letter when the surname is nothing but tussenvoegsels, so a
 * one-word name like "De" still yields a letter instead of nothing.
 */
function surnameInitial(lastname) {
  const words = String(lastname || "").trim().split(/\s+/).filter(Boolean);
  const significant = words.find((word) => !TUSSENVOEGSELS.has(normalise(word)));

  return firstLetter(significant || words[0] || "");
}

function firstLetter(word) {
  const match = String(word || "").match(/[\p{L}]/u);

  return match ? match[0].toUpperCase() : "";
}

/**
 * The initials shown when a Deelnemer has no profile picture: first letter of
 * the given name plus the first letter of the surname. Returns at most two
 * letters, and an empty string when there is no name at all — the caller then
 * has nothing to render but the empty avatar, which is still better than "?".
 */
export function initialsFor(person) {
  if (!person) return "";

  return (firstLetter(person.firstname) + surnameInitial(person.lastname)).slice(0, 2);
}

/** Full name for the tooltip and the dialog row, without double spaces. */
export function fullNameFor(person) {
  if (!person) return "";

  return [person.firstname, person.lastname]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
}

/**
 * Every Deelnemer across the whole payload, once each.
 *
 * Somebody booked on two tijdsloten of the same activity is one colleague, not
 * two avatars — the row says who is taking part, not how many bookings exist.
 * Order follows the payload, which the backend sorts by date, start time, name.
 */
export function uniqueParticipants(dates) {
  const seen = new Set();
  const people = [];

  for (const date of dates || []) {
    for (const slot of date.slots || []) {
      for (const person of slot.participants || []) {
        const id = person && person.user_id;
        if (id == null || seen.has(id)) continue;
        seen.add(id);
        people.push(person);
      }
    }
  }

  return people;
}

/** How many tijdsloten the payload holds in total, across every date. */
export function slotCount(dates) {
  return (dates || []).reduce((total, date) => total + (date.slots || []).length, 0);
}

/**
 * Whether the activity page shows the avatar row rather than the button.
 *
 * "Eenduidig" is exactly one date, one tijdslot and one location — judged over
 * the COMPLETE schedule the participants endpoint returns, never over the
 * client read's slot tree, which hides volgeboekte tijdsloten and would call a
 * three-date activity eenduidig as soon as two of them filled up.
 */
export function isSingleSlotActivity(dates) {
  const list = dates || [];
  if (list.length !== 1) return false;

  const slots = list[0].slots || [];
  if (slots.length !== 1) return false;

  return uniqueLocationCount(list) <= 1;
}

function uniqueLocationCount(dates) {
  const locations = new Set();
  for (const date of dates || []) {
    for (const slot of date.slots || []) {
      locations.add(slot.location_id);
    }
  }

  return locations.size;
}

/**
 * Split the avatar row into the faces that fit and the overflow count.
 *
 * The design caps the row and puts a "+N" bubble in the next position: ten
 * avatars on desktop, fourteen in the mobile grid. The bubble only appears when
 * it would stand for at least one person, so a row of exactly `limit` people
 * shows no bubble.
 */
export function splitForRow(people, limit) {
  const list = people || [];
  const max = Number(limit) > 0 ? Number(limit) : list.length;
  if (list.length <= max) {
    return { shown: list, overflow: 0 };
  }

  return { shown: list.slice(0, max), overflow: list.length - max };
}

/**
 * The Deelnemers of one **Tijdslot**, for a card in the Datumkiezer (ADR-0060).
 *
 * A datumkiezer card offers a single tijdslot, so it shows that tijdslot's
 * people — not the activity-wide row the activiteitenpagina draws. Keyed on
 * `slot_id`, which is `activity_location_specialist_slots.id`: the very id the
 * wizard builds its own slot tree on, so the join is exact.
 *
 * Compared as strings because the wizard's slot ids come out of the activity
 * JSON while the endpoint casts its own to int; `"41" !== 41` would silently
 * empty every row.
 */
export function participantsForSlot(dates, slotId) {
  const slot = findSlot(dates, slotId);

  return slot ? slot.participants || [] : [];
}

/**
 * Which activiteitsdatum a tijdslot sits on, so the popup opened from a card
 * unfolds on that card's date instead of on the first one (ADR-0060).
 */
export function dateIdForSlot(dates, slotId) {
  if (slotId == null) return null;
  const wanted = String(slotId);

  for (const date of dates || []) {
    for (const slot of date.slots || []) {
      if (String(slot.slot_id) === wanted) return date.date_id;
    }
  }

  return null;
}

function findSlot(dates, slotId) {
  if (slotId == null) return null;
  const wanted = String(slotId);

  for (const date of dates || []) {
    for (const slot of date.slots || []) {
      if (String(slot.slot_id) === wanted) return slot;
    }
  }

  return null;
}
